import { GoogleMap, LoadScriptNext } from "@react-google-maps/api";

import React, { useCallback, useContext, useEffect, useState } from "react";

import isEmpty from "lodash.isempty";
import { observer } from "mobx-react";
import * as PropTypes from "prop-types";
import styled from "styled-components";

import { useApi } from "../../../contexts/ApiContext";
import ComponentContext from "../../../contexts/ComponentContext";
import { useStores } from "../../../contexts/StoreContext";
import Markdown from "../../styles/Markdown";
import Loader from "../Loader/Loader";
import MapInfoBox from "./MapInfoBox/MapInfoBox";
import normalizeMarkerDetails from "./normalizeMarkerDetails/normalizeMarkerDetails";

const StyledFallback = styled.div`
  min-height: 40vh;
`;

function GMap({ styles, apiKey, markers, height }) {
  const [map, setMap] = useState(null);
  const hasOnlyOneMarker = markers.length === 1;

  const mapCenter = hasOnlyOneMarker
    ? markers[0].coords
    : { lat: 51.4857, lng: -0.96696 };

  useEffect(() => {
    if (map && !isEmpty(markers)) {
      const bounds = new window.google.maps.LatLngBounds();

      markers.forEach((marker) => {
        bounds.extend(
          new window.google.maps.LatLng(marker.coords.lat, marker.coords.lng)
        );
      });

      // Add padding to the bounds
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();

      // Adjust these values to increase/decrease the padding
      const latAdjust = 0.005; // latitude padding
      const lngAdjust = 0.005; // longitude padding

      const newBounds = new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(
          sw.lat() - latAdjust,
          sw.lng() - lngAdjust
        ),
        new window.google.maps.LatLng(
          ne.lat() + latAdjust,
          ne.lng() + lngAdjust
        )
      );

      map.fitBounds(newBounds);
    }
  }, [markers, map]);

  const onLoad = useCallback((mapInstance) => {
    setMap(mapInstance);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  if (isEmpty(markers)) {
    return null;
  }

  return (
    <LoadScriptNext
      googleMapsApiKey={apiKey}
      language="en"
      loadingElement={<Loader minHeight={height} />}
    >
      <div className="h-100 d-flex flex-column" id="hero-load-map">
        <div className="flex-grow-1">
          <GoogleMap
            center={mapCenter}
            className="flex-grow-1 flex-shrink-1"
            mapContainerStyle={{ height, width: "100%" }}
            onLoad={onLoad}
            onUnmount={onUnmount}
            options={{
              disableDefaultUI: true,
              fullscreenControl: false,
              styles,
              draggable: true,
              keyboardShortcuts: false,
              zoomControl: true,
              mapTypeControlOptions: false,
            }}
          >
            {markers.map((marker) => (
              <div key={marker.id}>
                <MapInfoBox state={marker} />
              </div>
            ))}
          </GoogleMap>
        </div>
      </div>
    </LoadScriptNext>
  );
}

GMap.propTypes = {
  apiKey: PropTypes.string.isRequired,
  height: PropTypes.string,
  markers: PropTypes.arrayOf(
    PropTypes.shape({
      city: PropTypes.string,
      coords: PropTypes.shape({
        lat: PropTypes.number,
        lng: PropTypes.number,
      }),
      displayName: PropTypes.string,
      formattedAddress: PropTypes.string,
      id: PropTypes.string,
      logoUrlFull: PropTypes.string,
      totalVacancies: PropTypes.number,
    })
  ),
  origin: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }),
};

GMap.defaultProps = {
  height: "500px",
  markers: [],
  origin: undefined,
};

function Map(props) {
  const { showText = true } = props;

  const { text, height } = useContext(ComponentContext);

  const { configStore } = useStores();
  const { googleMapsAPIKey, additionalConfiguration } = configStore.config;
  const [markerDetails, setMarkerDetails] = useState([]);
  const api = useApi();

  useEffect(() => {
    api
      .getVacancies({}, { fields: ["company", "location"], limit: 50 })
      .then((response) => {
        const details = normalizeMarkerDetails(response.data.results);
        setMarkerDetails(details);
      })
      .catch();
  }, []);

  return googleMapsAPIKey ? (
    <>
      {showText && <Markdown>{text}</Markdown>}
      <GMap
        {...props}
        height={height}
        apiKey={googleMapsAPIKey}
        markers={markerDetails}
        styles={additionalConfiguration?.mapStyles}
      />
    </>
  ) : (
    <StyledFallback className="text-center align-items-center d-flex justify-content-center">
      <h2>
        Sorry, the map is currently not available.
        <br />
        Please try again later.
      </h2>
    </StyledFallback>
  );
}

Map.propTypes = {
  apiKey: PropTypes.string,
  text: PropTypes.string,
  withMarkers: PropTypes.bool,
};

Map.defaultProps = {
  apiKey: null,
  text: "null",
  withMarkers: false,
};

export default observer(Map);
